/**
 * usePrimitiveRepositories - React Hook for V2 Primitive Repository Access
 *
 * Provides memoized access to all V2 primitive repositories with Apollo Client integration.
 * Follows FERP's repository pattern with 100% GraphQL field parity.
 *
 * Current Coverage: 5/25 primitives (20%)
 * - Actor (33 fields, 7Q + 7M)
 * - Approval (52 fields, 8Q + 6M)
 * - Instance (52 fields, 6Q + 10M)
 * - Notification (35 fields, 7Q + 5M)
 * - Template (37 fields, 7Q + 8M)
 *
 * @see frontend/packages/workflow-forms/src/data/repositories/primitives/
 * @see graphql_gateway/src/*_api/ for backend implementations
 */

import { useMemo, createContext, useContext, type ReactNode } from 'react';
import { useApolloClient } from '@apollo/client/react';
import type { ApolloClient } from '@apollo/client';

import {
  createAllPrimitiveRepositories,
  type AllPrimitiveRepositories,
  type ActorRepositoryV2,
  type ApprovalRepositoryV2,
  type InstanceRepositoryV2,
  type NotificationRepositoryV2,
  type TemplateRepositoryV2,
} from '../data/repositories/primitives';

// ============================================================================
// Context Definition
// ============================================================================

/**
 * Context for primitive repositories
 */
const PrimitiveRepositoriesContext = createContext<AllPrimitiveRepositories | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

interface PrimitiveRepositoriesProviderProps {
  children: ReactNode;
  /** Optional: Override Apollo Client (useful for testing) */
  client?: ApolloClient;
}

/**
 * Provider component that creates and provides V2 repositories to children.
 *
 * @example
 * ```tsx
 * // In your app root
 * import { PrimitiveRepositoriesProvider } from './hooks/usePrimitiveRepositories';
 *
 * function App() {
 *   return (
 *     <ApolloProvider client={apolloClient}>
 *       <PrimitiveRepositoriesProvider>
 *         <YourComponents />
 *       </PrimitiveRepositoriesProvider>
 *     </ApolloProvider>
 *   );
 * }
 * ```
 */
export function PrimitiveRepositoriesProvider({
  children,
  client: overrideClient,
}: PrimitiveRepositoriesProviderProps) {
  // Use provided client or get from Apollo context
  const contextClient = useApolloClient();
  const client = overrideClient || contextClient;

  // Memoize repository creation to avoid recreating on every render
  const repositories = useMemo(() => {
    return createAllPrimitiveRepositories(client as ApolloClient);
  }, [client]);

  return (
    <PrimitiveRepositoriesContext.Provider value={repositories}>
      {children}
    </PrimitiveRepositoriesContext.Provider>
  );
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Hook to access all V2 primitive repositories
 *
 * @returns Object containing all V2 repositories with 100% GraphQL field parity
 * @throws Error if used outside PrimitiveRepositoriesProvider
 *
 * @example
 * ```tsx
 * function ActorList() {
 *   const { actor } = usePrimitiveRepositories();
 *   const [actors, setActors] = useState<Actor[]>([]);
 *
 *   useEffect(() => {
 *     actor.findAllByTenant('tenant-1').then(result => {
 *       setActors(result.items);
 *     });
 *   }, [actor]);
 *
 *   return <div>{actors.map(a => <div key={a.id}>{a.name}</div>)}</div>;
 * }
 * ```
 */
export function usePrimitiveRepositories(): AllPrimitiveRepositories {
  const context = useContext(PrimitiveRepositoriesContext);

  if (!context) {
    throw new Error(
      'usePrimitiveRepositories must be used within PrimitiveRepositoriesProvider. ' +
        'Wrap your component tree with <PrimitiveRepositoriesProvider>.'
    );
  }

  return context;
}

// ============================================================================
// Individual Repository Hooks (Convenience)
// ============================================================================

/**
 * Hook to access Actor V2 repository only
 *
 * @example
 * ```tsx
 * const actorRepo = useActorRepository();
 * const actor = await actorRepo.findByTenantAndId('tenant-1', 'actor-123');
 * ```
 */
export function useActorRepository(): ActorRepositoryV2 {
  const { actor } = usePrimitiveRepositories();
  return actor;
}

/**
 * Hook to access Approval V2 repository only
 *
 * @example
 * ```tsx
 * const approvalRepo = useApprovalRepository();
 * const approval = await approvalRepo.findByTenantAndId('tenant-1', 'appr-456');
 * ```
 */
export function useApprovalRepository(): ApprovalRepositoryV2 {
  const { approval } = usePrimitiveRepositories();
  return approval;
}

/**
 * Hook to access Instance V2 repository only
 *
 * @example
 * ```tsx
 * const instanceRepo = useInstanceRepository();
 * const instance = await instanceRepo.findByTenantAndId('tenant-1', 'inst-789');
 * ```
 */
export function useInstanceRepository(): InstanceRepositoryV2 {
  const { instance } = usePrimitiveRepositories();
  return instance;
}

/**
 * Hook to access Notification V2 repository only
 *
 * @example
 * ```tsx
 * const notifRepo = useNotificationRepository();
 * const notifications = await notifRepo.findAllByTenant('tenant-1');
 * ```
 */
export function useNotificationRepository(): NotificationRepositoryV2 {
  const { notification } = usePrimitiveRepositories();
  return notification;
}

/**
 * Hook to access Template V2 repository only
 *
 * @example
 * ```tsx
 * const templateRepo = useTemplateRepository();
 * const templates = await templateRepo.findAllByTenant('tenant-1');
 * ```
 */
export function useTemplateRepository(): TemplateRepositoryV2 {
  const { template } = usePrimitiveRepositories();
  return template;
}

// ============================================================================
// Standalone Hook (No Provider Required)
// ============================================================================

/**
 * Create primitive repositories directly without context provider.
 * Useful for one-off usage or when context is not desirable.
 *
 * @returns Memoized repositories object
 *
 * @example
 * ```tsx
 * function StandaloneComponent() {
 *   const repos = usePrimitiveRepositoriesDirect();
 *   // Use repos.actor, repos.template, etc.
 * }
 * ```
 */
export function usePrimitiveRepositoriesDirect(): AllPrimitiveRepositories {
  const client = useApolloClient();

  return useMemo(() => {
    return createAllPrimitiveRepositories(client as ApolloClient);
  }, [client]);
}

// ============================================================================
// Type Re-exports for Convenience
// ============================================================================

export type {
  AllPrimitiveRepositories,
  ActorRepositoryV2,
  ApprovalRepositoryV2,
  InstanceRepositoryV2,
  NotificationRepositoryV2,
  TemplateRepositoryV2,
};
